/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
    XMarkIcon,
    CpuChipIcon,
    CircleStackIcon,
    ServerStackIcon,
    ClockIcon,
    CalculatorIcon,
    InformationCircleIcon,
    ChartBarIcon,
} from '@heroicons/react/24/outline';

type BillingTab = 'container' | 'storage' | 'volume';

interface BillingExplanationModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialTab?: BillingTab;
}

interface TabConfig {
    id: BillingTab;
    label: string;
    icon: React.ElementType;
    color: string;
}

const tabs: TabConfig[] = [
    { id: 'container', label: 'Containers', icon: CpuChipIcon, color: 'text-blue-400 bg-blue-500/20' },
    { id: 'storage', label: 'Object Storage', icon: CircleStackIcon, color: 'text-purple-400 bg-purple-500/20' },
    { id: 'volume', label: 'Volumes', icon: ServerStackIcon, color: 'text-green-400 bg-green-500/20' },
];

const BillingExplanationModal: React.FC<BillingExplanationModalProps> = ({
    isOpen,
    onClose,
    initialTab = 'container',
}) => {
    const [activeTab, setActiveTab] = useState<BillingTab>(initialTab);

    if (!isOpen) return null;

    const renderContainerBilling = () => (
        <div className="space-y-6">
            {/* Overview */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <CpuChipIcon className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-blue-300">Per-Minute Billing</h4>
                        <p className="text-sm text-blue-400/80 mt-1">
                            Containers are billed per minute based on CPU, Memory, and GPU resources allocated.
                        </p>
                    </div>
                </div>
            </div>

            {/* How it works */}
            <div>
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <CalculatorIcon className="w-5 h-5 text-gray-400" />
                    How Billing Works
                </h4>
                <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center flex-shrink-0">1</div>
                        <div>
                            <p className="font-medium text-white">Resource Selection</p>
                            <p className="text-sm text-gray-400">Choose CPU cores, RAM, and optional GPU type</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center flex-shrink-0">2</div>
                        <div>
                            <p className="font-medium text-white">Per-Minute Rate</p>
                            <p className="text-sm text-gray-400">Rate is calculated: (CPU rate + Memory rate + GPU rate) per minute</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center flex-shrink-0">3</div>
                        <div>
                            <p className="font-medium text-white">Usage Tracking</p>
                            <p className="text-sm text-gray-400">Billing starts when container is running, pauses when stopped</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Billing States */}
            <div>
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    Container States & Billing
                </h4>
                <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 border border-green-500/30 bg-green-500/10 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                            <span className="font-medium text-green-300">Running</span>
                        </div>
                        <p className="text-xs text-green-400/80">Billed per minute</p>
                    </div>
                    <div className="p-3 border border-gray-700 bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                            <span className="font-medium text-white">Stopped</span>
                        </div>
                        <p className="text-xs text-gray-400">No charges</p>
                    </div>
                    <div className="p-3 border border-yellow-500/30 bg-yellow-500/10 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                            <span className="font-medium text-yellow-300">Starting</span>
                        </div>
                        <p className="text-xs text-yellow-400/80">No charges until running</p>
                    </div>
                    <div className="p-3 border border-red-500/30 bg-red-500/10 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                            <span className="font-medium text-red-300">Failed</span>
                        </div>
                        <p className="text-xs text-red-400/80">No charges</p>
                    </div>
                </div>
            </div>

            {/* Example */}
            <div className="bg-gray-800 rounded-xl p-4">
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ChartBarIcon className="w-5 h-5 text-gray-400" />
                    Example Calculation
                </h4>
                <div className="bg-gray-900 rounded-lg p-3 text-sm">
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-400">4 vCPU @ ₹0.002/min</span>
                            <span className="font-medium text-white">₹0.008/min</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">16GB RAM @ ₹0.001/GB/min</span>
                            <span className="font-medium text-white">₹0.016/min</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">RTX 4090 @ ₹0.50/min</span>
                            <span className="font-medium text-white">₹0.50/min</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between font-semibold">
                            <span className="text-white">Total Rate</span>
                            <span className="text-blue-400">₹0.524/min (~₹31.44/hr)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStorageBilling = () => (
        <div className="space-y-6">
            {/* Overview */}
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <CircleStackIcon className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-purple-300">Pro-Rated Billing (Byte-Hours)</h4>
                        <p className="text-sm text-purple-400/80 mt-1">
                            Object storage is billed based on average usage over the billing period, ensuring fair charges even for temporary data.
                        </p>
                    </div>
                </div>
            </div>

            {/* How it works */}
            <div>
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <CalculatorIcon className="w-5 h-5 text-gray-400" />
                    How Pro-Rated Billing Works
                </h4>
                <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center flex-shrink-0">1</div>
                        <div>
                            <p className="font-medium text-white">Hourly Snapshots</p>
                            <p className="text-sm text-gray-400">System records your bucket size every hour</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center flex-shrink-0">2</div>
                        <div>
                            <p className="font-medium text-white">Average Calculation</p>
                            <p className="text-sm text-gray-400">At billing time, we calculate average usage from all snapshots</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center flex-shrink-0">3</div>
                        <div>
                            <p className="font-medium text-white">Fair Billing</p>
                            <p className="text-sm text-gray-400">You're charged for actual usage time, not just end-of-period size</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Why Pro-Rated */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <InformationCircleIcon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-amber-300">Why Pro-Rated Billing?</h4>
                        <p className="text-sm text-amber-400/80 mt-1">
                            If you upload 100GB, use it for 12 hours, then delete it - you're charged for 12 hours of 100GB storage,
                            not the full billing period. This ensures fair pricing for temporary data processing workflows.
                        </p>
                    </div>
                </div>
            </div>

            {/* Billing Periods */}
            <div>
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ClockIcon className="w-5 h-5 text-gray-400" />
                    Available Billing Periods
                </h4>
                <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 border border-gray-700 rounded-lg text-center">
                        <p className="font-medium text-white">Hourly</p>
                        <p className="text-xs text-gray-400 mt-1">Best for testing</p>
                    </div>
                    <div className="p-3 border border-purple-500/50 bg-purple-500/10 rounded-lg text-center">
                        <p className="font-medium text-purple-300">Monthly</p>
                        <p className="text-xs text-purple-400 mt-1">10% discount</p>
                    </div>
                    <div className="p-3 border border-gray-700 rounded-lg text-center">
                        <p className="font-medium text-white">Annual</p>
                        <p className="text-xs text-gray-400 mt-1">20% discount</p>
                    </div>
                </div>
            </div>

            {/* Example */}
            <div className="bg-gray-800 rounded-xl p-4">
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ChartBarIcon className="w-5 h-5 text-gray-400" />
                    Example: Pro-Rated Calculation
                </h4>
                <div className="bg-gray-900 rounded-lg p-3 text-sm">
                    <p className="text-gray-400 mb-3">Scenario: 50GB uploaded for 10 hours, then deleted (monthly billing)</p>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-400">Hourly snapshots recorded</span>
                            <span className="font-medium text-white">10 snapshots @ 50GB</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">Average usage</span>
                            <span className="font-medium text-white">~1.39GB (50GB x 10hrs / 720hrs)</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">Rate</span>
                            <span className="font-medium text-white">₹0.02/GB/hr</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between font-semibold">
                            <span className="text-white">Monthly Charge</span>
                            <span className="text-purple-400">₹10.00 (instead of ₹720 for full month)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderVolumeBilling = () => (
        <div className="space-y-6">
            {/* Overview */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <ServerStackIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-green-300">Provisioned Capacity Billing</h4>
                        <p className="text-sm text-green-400/80 mt-1">
                            Volumes are billed based on provisioned size, not actual usage. You pay for the capacity you allocate.
                        </p>
                    </div>
                </div>
            </div>

            {/* How it works */}
            <div>
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <CalculatorIcon className="w-5 h-5 text-gray-400" />
                    How Volume Billing Works
                </h4>
                <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0">1</div>
                        <div>
                            <p className="font-medium text-white">Volume Creation</p>
                            <p className="text-sm text-gray-400">Choose volume size (GB) and storage class (SSD/HDD)</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0">2</div>
                        <div>
                            <p className="font-medium text-white">Per-Hour Billing</p>
                            <p className="text-sm text-gray-400">Billed for provisioned size x hours the volume exists</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center flex-shrink-0">3</div>
                        <div>
                            <p className="font-medium text-white">Persistent Storage</p>
                            <p className="text-sm text-gray-400">Billing continues until volume is deleted (data persists)</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Volume Types */}
            <div>
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ServerStackIcon className="w-5 h-5 text-gray-400" />
                    Storage Classes
                </h4>
                <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 border border-green-500/30 bg-green-500/10 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg font-bold text-green-400">SSD</span>
                            <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">Fast</span>
                        </div>
                        <p className="text-sm text-green-400/80">High-performance NVMe storage</p>
                        <p className="text-xs text-green-400/60 mt-2">₹0.005/GB/hr (~₹3.60/GB/month)</p>
                    </div>
                    <div className="p-4 border border-gray-700 bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg font-bold text-gray-300">HDD</span>
                            <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">Economical</span>
                        </div>
                        <p className="text-sm text-gray-400">Cost-effective bulk storage</p>
                        <p className="text-xs text-gray-500 mt-2">₹0.002/GB/hr (~₹1.44/GB/month)</p>
                    </div>
                </div>
            </div>

            {/* Important Notes */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <InformationCircleIcon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-amber-300">Important Notes</h4>
                        <ul className="text-sm text-amber-400/80 mt-2 space-y-1 list-disc list-inside">
                            <li>Volumes are billed from creation until deletion</li>
                            <li>Data persists even when attached container is stopped</li>
                            <li>Snapshots are billed separately at reduced rates</li>
                            <li>Delete unused volumes to stop billing</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Example */}
            <div className="bg-gray-800 rounded-xl p-4">
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <ChartBarIcon className="w-5 h-5 text-gray-400" />
                    Example Calculation
                </h4>
                <div className="bg-gray-900 rounded-lg p-3 text-sm">
                    <p className="text-gray-400 mb-3">100GB SSD Volume for 30 days</p>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-400">Provisioned size</span>
                            <span className="font-medium text-white">100 GB</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">Duration</span>
                            <span className="font-medium text-white">720 hours (30 days)</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-400">Rate (SSD)</span>
                            <span className="font-medium text-white">₹0.005/GB/hr</span>
                        </div>
                        <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between font-semibold">
                            <span className="text-white">Monthly Cost</span>
                            <span className="text-green-400">₹360.00</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const modalContent = (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div>
                        <h2 className="text-xl font-bold text-white">Billing Explained</h2>
                        <p className="text-sm text-gray-400 mt-1">Understand how each resource is billed</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-600 hover:text-gray-400 hover:bg-gray-800 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-800 px-6">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    isActive
                                        ? 'border-primary-500 text-primary-400'
                                        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    {activeTab === 'container' && renderContainerBilling()}
                    {activeTab === 'storage' && renderStorageBilling()}
                    {activeTab === 'volume' && renderVolumeBilling()}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-800 bg-gray-800/50">
                    <p className="text-xs text-gray-400">
                        Prices shown are indicative. Check resource creation pages for current rates.
                    </p>
                    <button
                        onClick={onClose}
                        className="btn-primary"
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default BillingExplanationModal;
